/**
 *  Copyright AlpineReplay Inc, 2022. All rights reserved.
 *  Authors: Drew Burnett
 */

// Imports

import { isSmallMobileScreen, SaveToPlaylistModal, addClassName, removeClassName } from "trux"
import { useCreatePlaylistAndAddMoment } from "../../hooks/userCreatePlaylistAndAddMoment"
import { FindProfileDocument, useAddCollectionItemMutation } from "../../generated/graphql"
import { useLocation, useHistory } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import React, { useEffect, useState } from "react"
import { traceIdPlusUrl } from "../../util/links"
import "./WrappedSaveMomentFlow.css"

// Wrapped Save Moment Flow

const WrappedSaveMomentFlow = ({
    token,
    onClose
}) => {

    const [allRelevantUsersAndPlaylists, setAllRelevantUsersAndPlaylists] = useState([])
    const [destinationPlaylist, setDestinationPlaylist] = useState(null)
    const [loading, setLoading] = useState(false)
    const [wasOpen, setWasOpen] = useState(false) 
    const [open,] = useState(true)
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()

    const forceDropdown = location ? !location.pathname.includes("full-game") : false 

    // Are we mobile?
    const isMobileScreenSize = isSmallMobileScreen()

    // Get some data
    const loggedInUser = useSelector((state) => state.user.value)
    const loggedInUserProfile = useSelector((state) => state.userProfile.value)
    const familyProfiles = useSelector((state) => state.familyProfiles.value)

    // What moment should we ultimately save?
    let momentToSave = null
    // What moment is in Redux?
    const moment = useSelector((state) => { return state.video.value.source })
    // Is there a moment within the location.state?
    const momentFromState = location.state && location.state.momentToSave ? location.state.momentToSave : null
    if (momentFromState) {
        momentToSave = momentFromState 
    } else {
        momentToSave = moment
    }
    // Make sure that momentToSave isn't a full game moment
    if (momentToSave && (momentToSave.type === "FullGameVideo" || momentToSave.meta?.type === "FullGameVideo")) {
        // Redirect in this case (aka, don't allow saving!)
        handleOnClose()
    }

    const getPriorityPlaylist = (playlists) => {
        let result = null
        if (!playlists || playlists.length === 0) return result
        // Check goals first
        result = playlists.filter(playlist => playlist.id === "goals")
        // Then touch chains
        if (!result || result.length === 0) {
            result = playlists.filter(playlist => playlist.id === "touch-chains")
            // Then grab an arbitary playlist
            if ((!result || result.length === 0) && playlists) {
                result = playlists
            }
        }
        return result[0]
    }

    const createToastMessage = () => {
        let msg = ""
        let playlistLink = ""
        // Check the state to determine what should be included 
        const locationState = location && location.state ? location.state : null
        if (destinationPlaylist) {
            // If we selected a Trace iD then we want to mention that playlist
            // specifically in the bottom toast
            const playlistTitle = destinationPlaylist.title
            const playlistLink = traceIdPlusUrl(destinationPlaylist.userHashKey, destinationPlaylist.playlistHashKey, destinationPlaylist.userPlaylistClipId)
            msg = `<p>Your highlight was saved to <a href="${playlistLink}">${playlistTitle}</a>.</p>`
        } else if (locationState && locationState.playlists && locationState.playlists.length > 0) {
            const priorityPlaylist = getPriorityPlaylist(locationState.playlists)
            const playerTags = locationState.playerTags
            const newEvent = location.state.event
            if (priorityPlaylist) {
                const playlistTitle = priorityPlaylist.title
                const splitUrl = window.location.href.split("/")
                const urlWithoutPlaylist = window.location.href.includes("start=") ? splitUrl.splice(0, splitUrl.length - 4) : splitUrl.splice(0, splitUrl.length - 3)
                let playlistLink = urlWithoutPlaylist.join("/")
                playlistLink += ("/" + String(priorityPlaylist.id) + `/C${newEvent.game_clip_id}`)
                const playlistsCount = locationState.playlists.length
                msg = `<p>Your highlight was saved to <a href="${playlistLink}">${playlistTitle}</a>`
                if ((playlistsCount - 1) > 0) {
                    const playlistSuffix = (playlistsCount - 1) > 1 ? "s" : ""
                    const suffix = (playlistsCount - 1) > 1 ? ".</p>" : ".</p>"
                    msg += ` ${playerTags.length === 0 ? "and " : ", "}${playlistsCount - 1} tactical playlist${playlistSuffix}`
                    if (playerTags.length > 0) {
                        const playerSuffix = (playerTags.length > 1) ? "s" : ""
                        msg += `, and ${playerTags.length} player playlist${playerSuffix}${suffix}`
                    } else {
                        msg += suffix
                    }
                } else {
                    msg += "."
                }
            }
        }
        return [msg, playlistLink]
    }

    // Effects

    // Pause the video
    useEffect(() => {
        document.dispatchEvent(new CustomEvent("trc-pause"))
    })

    useEffect(() => {
        let newArr = []
        if (loggedInUserProfile) {
            let allUserCollections = []

            let loggedInUserCollections = (loggedInUserProfile && loggedInUserProfile.profile) ? loggedInUserProfile.profile.collections : []

            // Find out whether the "showcase" and "homework" playlists are present
            let foundHomework = loggedInUserCollections.find(loggedInUserCollection => loggedInUserCollection.title === "Homework")
            if (!foundHomework) {
                const homeworkPlaylist = { title: "Homework", needs_creation_on_select: true, public: true }
                allUserCollections.push(homeworkPlaylist)
            }
            let foundFamily = loggedInUserCollections.find(collection => collection.title === "Family")
            if (!foundFamily) {
                const familyPlaylist = { title: "Family", needs_creation_on_select: true, public: true }
                allUserCollections.push(familyPlaylist)
            }
            // If we have collection from the logged in user, push them also
            if (loggedInUserCollections) allUserCollections.push(...loggedInUserCollections)
            newArr.push({
                userProfile: loggedInUserProfile.profile,
                playlists: allUserCollections
            })
        }
        if (familyProfiles && familyProfiles.length > 0) {
            for (const familyProfile of familyProfiles) {
                newArr.push({
                    userProfile: familyProfile,
                    playlists: familyProfile.collections
                })
            }
        }
        if (newArr.length > 0) {
            setAllRelevantUsersAndPlaylists(newArr)
        }
    }, [loggedInUserProfile, familyProfiles])

    useEffect(() => {
        // When the destination playlist is set, we want to 
        // dismiss the dialog and do our thang!
        if (destinationPlaylist) {
            handleOnClose()
        }
    }, [destinationPlaylist])

    useEffect(() => {
        const locationState = location ? location.state : null
        let shouldToast = locationState ? true : false
        // Create and pass the appropriate message to redux depending
        // on why the dialog is being closed
        const [toastMessage, playlistLink] = createToastMessage()
        if (shouldToast) {
            dispatch({
                type: "COMMON_SET",
                key: "bottomToast",
                value: {
                    message: toastMessage, 
                    show: true, 
                    playlistLink: playlistLink,
                    link: null, 
                    linkText: null,
                    linkState: null
                }
            })
        }
    }, [])

    useEffect(() => {
        if (open && !wasOpen) {
            setWasOpen(true)
        } else if (!open && wasOpen) {
            setWasOpen(false)
        }
    }, [open])

    useEffect(() => {
        if (open && !wasOpen) {
            addClassName(document.body, "locked")
        } else if (!open && wasOpen) {
            removeClassName(document.body, "locked")
        }
    }, [open, wasOpen])

    // Hooks

    const createPlaylistAndAddMoment = useCreatePlaylistAndAddMoment(token)

    const [ addCollectionItem,] = useAddCollectionItemMutation({
        variables: {
            token: token,
        },
        refetchQueries: [FindProfileDocument, "findProfile"]
    })

    // Handlers

    const handleOnClose = () => {
        let shouldToast = false
        let destination = {}
        let linkText = "Trim, spotlight..."
        let finalLink = location.pathname.replace("save-moment", "personalize")
        if (typeof onClose === "function") {
            // Create and pass the appropriate message to redux depending
            // on why the dialog is being closed
            const [toastMessage, playlistLink] = createToastMessage()
            if (destinationPlaylist) {
                // The user selected and/or created a playlist. Determine
                // where the playlist was added and dispatch.
                shouldToast = true
                destination = {
                    userHashKey: destinationPlaylist.userHashKey,
                    playlistTitle: destinationPlaylist.title,
                    playlistHashKey: destinationPlaylist.playlistHashKey,
                    userPlaylistClipId: destinationPlaylist.userPlaylistClipId
                }
            }
            if (shouldToast) {
                dispatch({
                    type: "COMMON_SET",
                    key: "bottomToast",
                    value: {
                        message: toastMessage, 
                        show: true, 
                        playlistLink: playlistLink,
                        link: finalLink, 
                        linkText: linkText,
                        linkState: {
                            userHashKey: destination.userHashKey,
                            playlistTitle: destination.playlistTitle,
                            playlistHashKey: destination.playlistHashKey,
                            userPlaylistClipId: destination.userPlaylistClipId
                        }
                    }
                })
            }
            // Close the dialog
            onClose(history, destinationPlaylist)
        }
    }

    const handleOnDidSelectPlaylist = async (playlist, userProfile) => {
        // Add the moment to the playlist and dismiss
        setLoading(true)
        // Add the moment to the playlist
        const res = await addCollectionItem({
            variables: {
                hash_key: playlist.hash_key,
                add: {
                    user_playlist_clip_id: momentToSave.game_clip_id
                }
            }
        })
        if (res && res.data?.addCollectionItem?.success) {
            // Set the trace id destination playlist
            setDestinationPlaylist({
                title: playlist.title,
                userHashKey: userProfile.hash_key,
                playlistHashKey: playlist.hash_key,
                userPlaylistClipId: res.data.addCollectionItem.user_playlist_clip_id
            })
        } else {
            // Something went wrong...
            console.log("Error during add collection item: ", res)
        }
        // End loading
        setLoading(false)
    }

    const handleOnDidRequqestCreateNewPlaylist = async (playlist) => {
        // Start loading
        setLoading(true)
        // Create the playlist and add the moment
        const [profileHashKey, collectionHashKey, userPlaylistClipId] = await createPlaylistAndAddMoment(momentToSave, loggedInUserProfile.profile, playlist.name, playlist.private, playlist.color)
        // Set the trace id destination playlist
        if (profileHashKey && collectionHashKey && userPlaylistClipId) {
            setDestinationPlaylist({
                title: playlist.name,
                userHashKey: profileHashKey,
                playlistHashKey: collectionHashKey,
                userPlaylistClipId: userPlaylistClipId
            })
        }
        // End loading
        setLoading(false)
    }

    const handleOnDidRequestLockedPlaylist = (playlist, userProfile) => {
        // Show the upgrade dialog
        history.push({
            pathname: "goS/upgrade",
            state: { userId: userProfile.user_id },
        })
    }

    const handleOnDidRequestRelationLockedPlaylist = (playlist, userProfile) => {
        // Do nothing for now
        return
    }

    const locationState = location && location.state ? location.state : null
    const asWell = locationState && locationState.playlists ? locationState.playlists.length > 0 : false
    const forceStandardModal = location && location.state ? location.state.forceStandardModal : false

    // Return
    if ((isMobileScreenSize || forceDropdown) && !forceStandardModal) {
        return (
            <SaveToPlaylistModal 
                like="dropdown"
                open={open}
                forceLoading={loading}
                onClose={e => handleOnClose(e)}
                hideCloseButton={false}
                loggedInUser={loggedInUser}
                playlistsByUsers={allRelevantUsersAndPlaylists}
                onDidSelectPlaylist={handleOnDidSelectPlaylist}
                onDidRequestLockedPlaylist={handleOnDidRequestLockedPlaylist}
                onDidRequestRelationLockedPlaylist={handleOnDidRequestRelationLockedPlaylist}
                onDidRequestCreateNewPlaylist={handleOnDidRequqestCreateNewPlaylist}
                asWell={asWell}
            />
        )
    } else {
        return (
            <SaveToPlaylistModal 
                like="modal"
                open={open}
                forceLoading={loading}
                onClose={e => handleOnClose(e)}
                hideCloseButton={false}
                loggedInUser={loggedInUser}
                playlistsByUsers={allRelevantUsersAndPlaylists}
                onDidSelectPlaylist={handleOnDidSelectPlaylist}
                onDidRequestLockedPlaylist={handleOnDidRequestLockedPlaylist}
                onDidRequestRelationLockedPlaylist={handleOnDidRequestRelationLockedPlaylist}
                onDidRequestCreateNewPlaylist={handleOnDidRequqestCreateNewPlaylist}
                asWell={asWell}
            />
        )
    }

}

export { WrappedSaveMomentFlow }